import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { SEO } from "@/components/SEO";

const ROWS = [
  { label: "Entry", cheaper: "Climb over the tailgate", cabin: "Purpose-engineered, dust-sealed rear door" },
  { label: "Frame & engineering", cheaper: "Simpler frame construction", cabin: "Proprietary Bushtech aluminium frame technology, load-tested to 2.5 tonnes" },
  { label: "Side door opening size", cheaper: "Smaller side access openings", cabin: "Genuinely larger openings — only possible with Bushtech's more sophisticated frame engineering" },
  { label: "Insulation", cheaper: "Minimal or none", cabin: "NZ-spec Bush Cabins come fully insulated, with a foam-insulated cabin as standard" },
  { label: "Tent canvas", cheaper: "Single-layer, lower-grade canvas — either lower-grade poly-cotton, or sometimes polyester", cabin: "Premium South African canvas, dual-layer through the sides — built to the same standard that has seen EEZI-AWN canvas roof tents remain in service for 30+ years, a level of durability rarely seen in the NZ market" },
  { label: "Mattress", cheaper: "60–70mm", cabin: "100mm premium EEZI-AWN foam, purpose-designed to be the best in its class of camper" },
  { label: "Living while sleeping", cheaper: "Tent section often dimensioned like a standard roof tent — if one person needs to get up, even for a middle-of-the-night bathroom trip, both do", cabin: "Genuinely live on both levels at once — sleep 2 (up to approx. 6ft tall) up top while 2 more cook, relax or shelter from the weather downstairs, without anyone needing to wake, dress, or step outside" },
  { label: "Brand pedigree", cheaper: "Often a generic product, locally branded", cabin: "Bushtech + EEZI-AWN — decades proven across Africa and every major continent" },
  { label: "Resale appeal", cheaper: "Access and liveability compromises may narrow future buyer appeal", cabin: "Easy rear-door access and genuine live-in usability broaden resale appeal and potential value" },
];

export default function BushCabinCheaperComparisonPage() {
  return (
    <div data-testid="bushcabin-cheaper-comparison-page">
      <SEO
        path="/bush-cabin/why-not-a-cheaper-camper"
        title="Bush Cabin vs Cheaper Ute Campers — What The Price Difference Actually Buys You"
        description="A practical comparison of the Bushtech Bush Cabin against cheaper ute campers — engineering, materials, mattress, access, warranty and true build cost."
        noindex
      />

      <section className="container-x pt-32 pb-16 md:pt-40 md:pb-20">
        <p className="overline mb-4">— Bush Cabin vs. Cheaper Ute Campers</p>
        <h1 className="font-display text-4xl md:text-6xl text-white leading-[0.95] max-w-4xl">
          CHEAPER CAMPER.<br />
          <span className="text-[var(--brand-primary)]">DIFFERENT CLASS</span> OF PRODUCT.
        </h1>
        <p className="text-[var(--text-secondary)] text-lg md:text-xl mt-6 max-w-2xl leading-relaxed">
          On the surface, cheaper ute campers and a purpose-engineered camper like the Bush Cabin can look like different price points on the same product. They're not. Underneath the photos, they're different categories of product entirely — and the gap shows up fast once you look at the engineering, construction and real-world usability.
        </p>
      </section>

      {/* COMPARISON TABLE */}
      <section className="container-x pb-16 md:pb-20">
        <div className="overflow-x-auto border border-[var(--border-default)]" data-testid="bushcabin-comparison-table">
          <table className="w-full min-w-[720px] text-left border-collapse">
            <thead>
              <tr className="bg-[var(--bg-secondary)]">
                <th className="p-4 md:p-5 font-mono text-xs uppercase tracking-[0.15em] text-[var(--text-tertiary)] w-1/5">&nbsp;</th>
                <th className="p-4 md:p-5 font-mono text-xs uppercase tracking-[0.15em] text-white/70 w-2/5">Cheaper Ute Camper</th>
                <th className="p-4 md:p-5 font-mono text-xs uppercase tracking-[0.15em] text-[var(--brand-primary)] w-2/5 bg-[var(--brand-primary)]/[0.06]">Bush Cabin</th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map((r, i) => (
                <tr key={r.label} data-testid={`comparison-row-${i}`} className="border-t border-[var(--border-default)]">
                  <td className="p-4 md:p-5 text-sm md:text-base text-white font-semibold align-top">{r.label}</td>
                  <td className="p-4 md:p-5 text-sm md:text-base text-[var(--text-secondary)] leading-relaxed align-top">
                    {r.cheaper}
                  </td>
                  <td className="p-4 md:p-5 text-sm md:text-base text-white leading-relaxed align-top bg-[var(--brand-primary)]/[0.04]">
                    <span className="flex items-start gap-2.5">
                      <Check size={16} className="text-[var(--brand-primary)] shrink-0 mt-0.5" strokeWidth={2.5} />
                      {r.cabin}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* CONCLUSION */}
      <section className="border-t border-[var(--border-default)] bg-[var(--bg-secondary)]">
        <div className="container-x py-16 md:py-24">
          <div className="max-w-3xl">
            <p className="overline mb-4">— The Real Cost Comparison</p>
            <h2 className="font-display text-3xl md:text-5xl text-white leading-[0.95] mb-8">
              A CHEAPER CAMPER IS SOMETIMES RIGHT.<br />
              <span className="text-[var(--brand-primary)]">A FULL BUILD IS A DIFFERENT CALL.</span>
            </h2>
            <div className="space-y-5 text-base md:text-lg text-[var(--text-secondary)] leading-relaxed">
              <p>
                Cheaper campers absolutely have their place. They can genuinely make sense for younger buyers working with a tighter budget, or matched to an older vehicle — there's nothing wrong with spending in proportion to what's underneath the camper.
              </p>
              <p>
                But if you're undertaking a <span className="text-white font-semibold">full ute camper build</span> — buying and preparing or upgrading a vehicle, fitting a camper, and completing an extensive interior fit-out — you're already committing to a considerable project cost either way. Set against that total, the money saved by choosing the cheaper camper becomes fairly immaterial.
              </p>
              <p>
                What you're left with, though, is a vehicle built around real compromises — and one with limited resale appeal to the largest pool of buyers in New Zealand likely to want it. For a full build, prioritising a lower price on the camper is often a <span className="text-white font-semibold">false economy</span>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="container-x py-16 md:py-24">
        <div className="border border-[var(--border-default)] p-8 md:p-12 max-w-3xl">
          <p className="text-white text-lg md:text-xl leading-relaxed">
            Planning a full ute camper build and want to talk through what actually makes sense for your vehicle and budget? Get in touch — we're happy to walk you through it directly.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/bush-cabin#enquire" data-testid="comparison-cta-enquire" className="btn-primary">
              Ask Us Anything <ArrowUpRight size={14} />
            </Link>
            <Link to="/bush-cabin" data-testid="comparison-cta-view-bushcabin" className="btn-secondary">
              View The Bush Cabin
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
